import { DKGridTable } from "../../../common/grid/DKGridTable";
import * as api from "../../../../api/generated-api";
import React, { useEffect, useState } from "react";
import { DKGridColumn } from "../../../common/grid/DKGridColumn";
import { Grid, SxProps, Typography, useTheme } from "@mui/material";
import { DealLegalEntitySelect } from "../DealLegalEntitySelect";
import {
  currencyFormat,
  formatNumber,
  getExactWireAmount,
  getInternalSplit,
} from "../../../../helpers/utils";
import DKFormMoneyInput from "../../../common/widgets/formVariablesWidget/DKFormMoneyInput";
import DKFormPercentageInput from "../../../common/widgets/formVariablesWidget/DKFormPercentageInput";
import { DealPageActionButtonInfo } from "../../dealsPageLayout/DealsPageHeader/DealsPageHeader";
import {
  DealInvestorsFormError,
  DealLegalEntityInvestmentFormEntry,
} from "../DealInvestorDetailPage";
import DKFormNumberInput from "../../../common/widgets/formVariablesWidget/DKFormNumberInput";
import { DKGridFooterColumn } from "../../../common/grid/DKGridFooterColumn";
import { InvestmentSignatureBlockRowAction } from "../InvestmentSignatureBlockRowAction";
import InvestorRowCellWrapper from "../InvestorRowCellWrapper";
import RemoveIcon from "../../../common/icons/removeIcon";
import InvestorActionCellWrapper from "../InvestorActionCellWrapper";

type NewCashInvestmentsGridProps = {
  orgId: number;
  dealId: number;
  dealInvestorId: number;
  editMode: boolean;
  items: Map<number, DealLegalEntityInvestmentFormEntry>;
  dealLegalEntitiesOfInvestor: api.DealLegalEntity[];
  pricePerShare: number;
  totalInvestments: number;
  totalPercentage: number;
  investorTotalInvestments: number;
  canEditInvestorInfo: boolean;
  errors: DealInvestorsFormError;
  onEdit: () => void;
  onAdd: () => void;
  onCancel: () => void;
  onSave: () => void;
  onRemove: (
    dealLegalEntityInvestmentForEntry: DealLegalEntityInvestmentFormEntry
  ) => void;
  onChangeItem: (
    changedItem: DealLegalEntityInvestmentFormEntry,
    allItems: DealLegalEntityInvestmentFormEntry[]
  ) => void;
};

export const NewCashInvestmentsGrid = (props: NewCashInvestmentsGridProps) => {
  const {
    orgId,
    dealId,
    dealInvestorId,
    editMode,
    items,
    dealLegalEntitiesOfInvestor,
    pricePerShare,
    totalInvestments,
    totalPercentage,
    investorTotalInvestments,
    canEditInvestorInfo,
    errors,
    onEdit,
    onAdd,
    onCancel,
    onSave,
    onRemove,
    onChangeItem,
  } = props;

  const theme = useTheme();

  const [rows, setRows] = useState<DealLegalEntityInvestmentFormEntry[]>(
    Array.from(items.values())
  );

  useEffect(() => {
    setRows(Array.from(items.values()));
  }, [items]);

  const updateRowValue = (
    index: number,
    value: DealLegalEntityInvestmentFormEntry
  ) => {
    const newRows = [...rows];
    newRows[index] = value;
    setRows(newRows);
    onChangeItem(value, newRows);
  };

  const actionButtons: DealPageActionButtonInfo[] = [];

  if (!editMode && canEditInvestorInfo) {
    actionButtons.push({
      dataTestId: "edit-new-cash-investment-button",
      label: "edit",
      onClick: () => {
        onEdit();
      },
    });
  }

  if (editMode) {
    actionButtons.push({
      dataTestId: "add-new-cash-investment-button",
      label: "add more",
      onClick: () => {
        onAdd();
      },
    });

    actionButtons.push({
      dataTestId: "cancel-new-cash-investment-button",
      label: "cancel",
      onClick: () => {
        onCancel();
      },
    });

    actionButtons.push({
      dataTestId: "save-new-cash-investment-button",
      label: "save",
      disabled: !!errors && !!errors.internalSplit,
      onClick: () => {
        onSave();
      },
    });
  }

  const styles: SxProps = {
    fontWeight: "bold",
  };

  const Footer = () => {
    if (rows.length == 0) {
      return null;
    }

    const showingRemaining = totalInvestments < investorTotalInvestments;

    const totalExactWireAmount = rows.reduce((previousValue, currentValue) => {
      const currentExactWireAmount =
        Number.isNaN(currentValue.exactWireAmount) ||
        currentValue.exactWireAmount == undefined
          ? 0
          : currentValue.exactWireAmount;

      return currentExactWireAmount + previousValue;
    }, 0);

    const totalShares = rows.reduce((previousValue, currentValue) => {
      const currentShares =
        Number.isNaN(currentValue.shares) || currentValue.shares == undefined
          ? 0
          : currentValue.shares;
      return currentShares + previousValue;
    }, 0);

    return (
      <>
        <Grid container spacing={2}>
          <DKGridFooterColumn
            width={3}
            title={"Total"}
            dataTestId={"total-investing-row-title"}
          />
          <DKGridFooterColumn
            dataTestId={"total-investing-row-total"}
            width={2}
            title={currencyFormat(totalInvestments)}
          >
            {showingRemaining && (
              <Typography
                variant={"body1"}
                sx={{ ...styles, marginTop: "10px" }}
              >
                {currencyFormat(investorTotalInvestments - totalInvestments)}{" "}
                remaining
              </Typography>
            )}
          </DKGridFooterColumn>
          <DKGridFooterColumn
            width={2}
            title={totalPercentage + "%"}
            dataTestId={"total-investing-row-percentage"}
          />
          <DKGridFooterColumn
            width={2}
            title={currencyFormat(totalExactWireAmount)}
          />
          <DKGridFooterColumn width={2} title={formatNumber(totalShares)} />
          <DKGridFooterColumn width={1} title={""} />
        </Grid>
        {errors && (
          <Grid container spacing={2}>
            <DKGridFooterColumn width={3} title={""} />
            <DKGridFooterColumn
              dataTestId={"new-cash-investment-errors"}
              width={9}
              title={errors.internalSplit}
              color={theme.palette.error.main}
            />
          </Grid>
        )}
      </>
    );
  };

  return (
    <DKGridTable
      title={"New Investment"}
      rowsLength={rows.length}
      actionButtons={actionButtons}
      footer={<Footer />}
    >
      <DKGridColumn width={3} title={"Investing Entity Legal Name"}>
        {rows.map((item, itemIndex) => {
          if (!editMode) {
            return (
              <InvestorRowCellWrapper
                key={"new-cash-investment-name-" + itemIndex}
              >
                <Typography
                  variant={"body1"}
                  data-testid={"new-cash-investment-name"}
                >
                  {item.name}
                </Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DealLegalEntitySelect
                dataTestId={"new-cash-investment-name-field-" + itemIndex}
                key={"name-" + itemIndex}
                dealLegalEntities={dealLegalEntitiesOfInvestor}
                selectedDealLegalEntityId={item.dealInvestorMemberSignerId}
                onChange={(event) => {
                  updateRowValue(itemIndex, {
                    ...rows[itemIndex],
                    dealInvestorMemberSignerId: Number(event.target.value),
                  });
                }}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={2} title={"Target Investment"}>
        {rows.map((item, itemIndex) => {
          if (!editMode) {
            return (
              <InvestorRowCellWrapper key={"targetCashInvestment-" + itemIndex}>
                <Typography
                  variant={"body1"}
                  data-testid={"new-cash-investment-targetCashInvestment"}
                >
                  {item?.targetCashInvestment &&
                    currencyFormat(item.targetCashInvestment)}
                </Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DKFormMoneyInput
                dataTestId={
                  "new-cash-investment-targetCashInvestment-field-" + itemIndex
                }
                key={"targetCashInvestment-" + itemIndex}
                name={"targetCashInvestment"}
                label={""}
                value={item.targetCashInvestment}
                onChange={(targetCashInvestment, sourceInfo) => {
                  //Note: Using the constant here causes the app to not compile for reasons I do not understand
                  //Note: The "event" type of source is used to tell us that the user made the change, not that the
                  //      property was updated externally.
                  if (sourceInfo.source == "event") {
                    const shares = Math.floor(
                      targetCashInvestment / pricePerShare
                    );

                    const exactWireAmount = Number(
                      getExactWireAmount(shares.toFixed(2), pricePerShare)
                    );

                    const split = Number(
                      getInternalSplit(
                        targetCashInvestment,
                        investorTotalInvestments
                      )
                    );

                    const updatedRow = {
                      ...item,
                      split,
                      targetCashInvestment,
                      shares,
                      exactWireAmount,
                    };

                    updateRowValue(itemIndex, updatedRow);
                  }
                }}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={2} title={"Internal Split"}>
        {rows.map((item, itemIndex) => {
          if (!editMode) {
            return (
              <InvestorRowCellWrapper key={"split-" + itemIndex}>
                <Typography
                  variant={"body1"}
                  data-testid={"new-cash-investment-split"}
                >
                  {item.split}%
                </Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DKFormPercentageInput
                data-testid={"new-cash-investment-split-field-" + itemIndex}
                key={"split-" + itemIndex}
                name={"split"}
                label={""}
                value={item.split}
                maxDecimalPlaces={5}
                onChange={(split, sourceInfo) => {
                  //Note: Using the constant here causes the app to not compile for reasons I do not understand
                  if (sourceInfo.source == "event") {
                    const targetCashInvestment =
                      (investorTotalInvestments / 100) * split;
                    const shares = Math.floor(
                      targetCashInvestment / pricePerShare
                    );
                    const exactWireAmount = Number(
                      getExactWireAmount(shares.toFixed(2), pricePerShare)
                    );

                    const updatedRow = {
                      ...item,
                      shares,
                      exactWireAmount,
                      split,
                      targetCashInvestment,
                    };

                    updateRowValue(itemIndex, updatedRow);
                  }
                }}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={2} title={"Exact Wire Amount"}>
        {rows.map((item, itemIndex) => {
          if (!editMode) {
            return (
              <InvestorRowCellWrapper key={"exactWireAmount-" + itemIndex}>
                <Typography
                  variant={"body1"}
                  data-testid={"new-cash-investment-exactWireAmount"}
                >
                  {currencyFormat(item.exactWireAmount)}
                </Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DKFormMoneyInput
                data-testid={
                  "new-cash-investment-exactWireAmount-field-" + itemIndex
                }
                disabled={true}
                key={"exactWireAmount-" + itemIndex}
                name={"exactWireAmount"}
                label={""}
                value={item.exactWireAmount}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={2} title={"Shares"}>
        {rows.map((item, itemIndex) => {
          if (!editMode) {
            return (
              <InvestorRowCellWrapper key={"shares-" + itemIndex}>
                <Typography
                  variant={"body1"}
                  data-testid={"new-cash-investment-shares"}
                >
                  {formatNumber(item.shares)}
                </Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DKFormNumberInput
                data-testid={"new-cash-investment-shares-field-" + itemIndex}
                key={"shares-" + itemIndex}
                name={"shares"}
                label={""}
                value={item.shares}
                disabled={true}
                maxDecimalPlaces={0}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={1} title={" "}>
        {rows.map((item, itemIndex) => {
          if (!editMode) {
            return (
              <InvestorRowCellWrapper
                key={"new-cash-investment-signature-block-link-" + itemIndex}
              >
                <InvestorActionCellWrapper>
                  <InvestmentSignatureBlockRowAction
                    dataTestId={
                      "new-cash-investment-signature-block-link-" + itemIndex
                    }
                    orgId={orgId}
                    dealId={dealId}
                    dealInvestorId={dealInvestorId}
                    dealLegalEntityInvestmentId={item.id}
                    itemIndex={itemIndex}
                  />
                </InvestorActionCellWrapper>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <InvestorRowCellWrapper key={"row-actions-" + itemIndex} editMode>
                <InvestorActionCellWrapper>
                  <RemoveIcon
                    data-testid={"row-action-delete-new-cash-investment"}
                    fill={
                      !item.wireDateReceived
                        ? theme.palette.secondary.darker
                        : theme.palette.tertiary.dark
                    }
                    onClick={() => {
                      !item.wireDateReceived ? onRemove(item) : undefined;
                    }}
                  />
                </InvestorActionCellWrapper>
              </InvestorRowCellWrapper>
            );
          }
        })}
      </DKGridColumn>
    </DKGridTable>
  );
};
